import * as Sentry from '@sentry/react';
import { ApiError } from '@/lib/api';

export type SentryReportContext = {
  source?: 'query' | 'mutation';
  queryKey?: unknown;
  mutationKey?: unknown;
};

const CLIENT_VALIDATION_PATTERNS = [
  /^Please enter /i,
  /^Please select /i,
  /^No household found/i,
  /^User not ready/i,
  /^No co-parent found/i,
];

// Offline / proxy HTML bodies — not app bugs. HARBOR-WEB-11 / HARBOR-WEB-16.
export const TRANSIENT_NETWORK_ERROR_PATTERNS = [
  /^Failed to fetch$/i,
  /^Network request failed$/i,
  /^Network request timed out\.?$/i,
  /^Load failed$/i,
  /^Network Error$/i,
  /^NetworkError when attempting to fetch resource/i,
];

export function isTransientClientNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  const message = err.message.trim();
  if (TRANSIENT_NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }
  // apiJson JSON.parse of an HTML error/index page (HARBOR-WEB-16).
  return err instanceof SyntaxError && /Unexpected token ['"]?</.test(message);
}

export function shouldReportError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status >= 400 && err.status < 500) {
      return false;
    }
    if (/unexpected HTML response/i.test(err.message)) {
      return false;
    }
    return true;
  }

  if (isTransientClientNetworkError(err)) {
    return false;
  }

  if (err instanceof Error) {
    const message = err.message.trim();
    if (CLIENT_VALIDATION_PATTERNS.some((pattern) => pattern.test(message))) {
      return false;
    }
  }

  return true;
}

export function reportError(err: unknown, context?: SentryReportContext): void {
  if (!shouldReportError(err)) {
    return;
  }

  Sentry.captureException(err, {
    extra: context,
    tags: context?.source ? { source: context.source } : undefined,
  });
}
